/**
 * Every open balance, worst first. Reuses `BalanceTable` — the same
 * component the dashboards feed from `/api/me` — because the rent roll
 * endpoint returns the identical shape by design (see the service).
 */

import { useState } from 'react';
import { useList } from '@refinedev/core';
import type { SessionBalance } from 'shared';
import { BalanceTable } from '../../../components/BalanceTable.js';

interface PropertyOption {
  id: string;
  name: string;
}

export function RentRoll() {
  const [propertyId, setPropertyId] = useState('');
  const [asOf, setAsOf] = useState('');

  const { data: propertiesData } = useList<PropertyOption>({
    resource: 'properties',
    pagination: { current: 1, pageSize: 200 },
  });

  const { data, isLoading, error } = useList<SessionBalance>({
    resource: 'ledger/rent-roll',
    pagination: { mode: 'off' },
    filters: [
      { field: 'propertyId', operator: 'eq', value: propertyId || undefined },
      { field: 'asOf', operator: 'eq', value: asOf || undefined },
    ],
  });

  return (
    <section>
      <h1>Rent roll</h1>
      <p className="muted">
        Every lease with a balance, worst first. Derived from unreversed allocations — never stored
        — as of the date below (today, unless you pick one).
      </p>

      <div className="row">
        <select
          className="field__input"
          value={propertyId}
          onChange={(event) => setPropertyId(event.target.value)}
        >
          <option value="">All properties</option>
          {(propertiesData?.data ?? []).map((property) => (
            <option key={property.id} value={property.id}>
              {property.name}
            </option>
          ))}
        </select>
        <input
          className="field__input"
          type="date"
          value={asOf}
          onChange={(event) => setAsOf(event.target.value)}
        />
      </div>

      {isLoading ? <p className="muted">Loading…</p> : null}
      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load the rent roll.'}
        </p>
      ) : null}
      {!isLoading && !error ? (
        <BalanceTable
          balances={data?.data ?? []}
          emptyMessage="Nothing outstanding as of this date."
        />
      ) : null}
    </section>
  );
}
